import React, { useEffect, useState, useRef } from "react";
import { Routes, Route, Link, useParams, useNavigate } from "react-router-dom";
import { api, API, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { LayoutDashboard, Calendar, BookOpen, Users, Settings, BarChart3, Plus, Trash2, Download, QrCode, Upload, ExternalLink, Copy, FileText, Image as ImageIcon, MessageSquare, ArrowLeft, User as UserIcon, Bot, PenSquare, Ticket } from "lucide-react";
import { DashboardShell, StatCard, SectionTitle, Btn, Input, Modal, EmptyState } from "@/components/DashUI";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import ReactMarkdown from "react-markdown";
import { LeadForms } from "@/pages/LeadFormsAdmin";
import { VisitorManagement } from "@/pages/VisitorManagement";

const NAV = [
  { to: "/dashboard", label: "Overview", icon: <LayoutDashboard size={16} /> },
  { to: "/dashboard/events", label: "Events", icon: <Calendar size={16} />, match: "/dashboard/events" },
  { to: "/dashboard/lead-forms", label: "Lead Forms", icon: <PenSquare size={16} />, match: "/dashboard/lead-forms" },
  { to: "/dashboard/visitor-management", label: "Visitor Management", icon: <Ticket size={16} />, match: "/dashboard/visitor-management" },
  { to: "/dashboard/knowledge", label: "Knowledge Base", icon: <BookOpen size={16} /> },
  { to: "/dashboard/conversations", label: "Conversations", icon: <MessageSquare size={16} />, match: "/dashboard/conversations" },
  { to: "/dashboard/leads", label: "Leads", icon: <Users size={16} /> },
  { to: "/dashboard/ai", label: "AI Settings", icon: <Settings size={16} /> },
  { to: "/dashboard/analytics", label: "Analytics", icon: <BarChart3 size={16} /> },
];

export function OrgAdmin() {
  return (
    <Routes>
      <Route index element={<Overview />} />
      <Route path="events" element={<EventsList />} />
      <Route path="events/:eventId" element={<EventDetail />} />
      <Route path="lead-forms/*" element={<LeadForms nav={NAV} />} />
      <Route path="visitor-management/*" element={<VisitorManagement nav={NAV} />} />
      <Route path="knowledge" element={<KnowledgeBase />} />
      <Route path="conversations" element={<ConversationsList />} />
      <Route path="conversations/:convId" element={<ConversationDetail />} />
      <Route path="leads" element={<LeadsList />} />
      <Route path="ai" element={<AISettings />} />
      <Route path="analytics" element={<Analytics />} />
    </Routes>
  );
}

/* ==================== Overview ==================== */
function Overview() {
  const [stats, setStats] = useState(null);
  const [org, setOrg] = useState(null);
  useEffect(() => {
    api.get("/analytics").then((r) => setStats(r.data));
    api.get("/auth/me").then(async (u) => {
      if (u.data.org_id) {
        const o = await api.get(`/organizations/${u.data.org_id}`);
        setOrg(o.data);
      }
    });
  }, []);
  const lf = stats?.lead_forms || {};
  const vm = stats?.visitor_management || {};
  return (
    <DashboardShell title={org?.name || "Dashboard"} subtitle="Overview" nav={NAV} testId="org-overview">
      {/* AI Assistant / Events module */}
      <div className="text-[10px] uppercase tracking-[0.18em] text-gray-400 font-bold mb-3">AI Assistant & Events</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Events" value={stats?.events} testId="stat-events" />
        <StatCard label="Conversations" value={stats?.conversations} testId="stat-conversations" />
        <StatCard label="Questions asked" value={stats?.questions_asked} testId="stat-questions" />
        <StatCard label="Leads" value={stats?.leads} testId="stat-leads" />
      </div>

      {/* Lead Forms module */}
      <div className="text-[10px] uppercase tracking-[0.18em] text-gray-400 font-bold mt-10 mb-3">Lead Forms</div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatCard label="Forms" value={lf.forms ?? 0} testId="stat-lf-forms" />
        <StatCard label="Views" value={lf.views ?? 0} testId="stat-lf-views" />
        <StatCard label="Starts" value={lf.starts ?? 0} testId="stat-lf-starts" />
        <StatCard label="Completions" value={lf.completions ?? 0} testId="stat-lf-completions" />
        <StatCard label="Completion rate" value={lf.completion_rate != null ? `${lf.completion_rate}%` : "—"} testId="stat-lf-rate" />
      </div>

      {/* Visitor Management module */}
      <div className="text-[10px] uppercase tracking-[0.18em] text-gray-400 font-bold mt-10 mb-3">Visitor Management</div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatCard label="Events" value={vm.events ?? 0} testId="stat-vm-events" />
        <StatCard label="Attendees" value={vm.attendees ?? 0} testId="stat-vm-attendees" />
        <StatCard label="Pending approvals" value={vm.pending ?? 0} testId="stat-vm-pending" />
        <StatCard label="Checked in" value={vm.checked_in ?? 0} testId="stat-vm-checkedin" />
        <StatCard label="Check-in rate" value={vm.check_in_rate != null ? `${vm.check_in_rate}%` : "—"} testId="stat-vm-rate" />
      </div>

      <div className="mt-10 border border-gray-200 rounded-xl p-6">
        <div className="text-[10px] uppercase tracking-[0.18em] text-gray-400 font-bold">Quick actions</div>
        <div className="flex flex-wrap gap-3 mt-4">
          <Link to="/dashboard/events"><Btn><Plus size={14} /> New event</Btn></Link>
          <Link to="/dashboard/lead-forms"><Btn variant="secondary"><PenSquare size={14} /> New lead form</Btn></Link>
          <Link to="/dashboard/visitor-management"><Btn variant="secondary"><Ticket size={14} /> Visitor event</Btn></Link>
          <Link to="/dashboard/knowledge"><Btn variant="secondary"><Upload size={14} /> Upload knowledge</Btn></Link>
          <Link to="/dashboard/ai"><Btn variant="secondary"><Settings size={14} /> Configure assistant</Btn></Link>
        </div>
      </div>
    </DashboardShell>
  );
}

/* ==================== Events ==================== */
function EventsList() {
  const [rows, setRows] = useState([]);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ name: "", slug: "", venue: "", description: "", start_date: "", end_date: "", areas_of_interest: [] });
  const [newArea, setNewArea] = useState("");
  const nav = useNavigate();
  async function refresh() { const { data } = await api.get("/events"); setRows(data); }
  useEffect(() => { refresh(); }, []);

  async function create(e) {
    e.preventDefault();
    try { await api.post("/events", form); toast.success("Event created"); setModal(false); setForm({ name: "", slug: "", venue: "", description: "", start_date: "", end_date: "", areas_of_interest: [] }); setNewArea(""); refresh(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  }

  async function del(id) { if (!window.confirm("Delete event?")) return; await api.delete(`/events/${id}`); refresh(); }

  return (
    <DashboardShell title="Events" subtitle="Events" nav={NAV}>
      <SectionTitle title="Your events" action={<Btn onClick={() => setModal(true)} testId="new-event-btn"><Plus size={14} /> New event</Btn>} />
      {rows.length === 0 ? <EmptyState icon={<Calendar size={18} />} title="No events yet" body="Create your first event to generate a QR code." action={<Btn onClick={() => setModal(true)}><Plus size={14} /> New event</Btn>} /> :
        <div className="grid md:grid-cols-2 gap-4">
          {rows.map((e) => (
            <div key={e.id} className="border border-gray-200 rounded-xl p-5 hover:border-gray-300 transition-colors">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-gray-400">{e.status}</div>
                  <div className="font-display text-lg font-semibold text-gray-900 mt-1">{e.name}</div>
                  <div className="text-sm text-gray-500 mt-1">{e.venue || "—"}</div>
                  <div className="text-xs text-gray-400 mt-2">{e.start_date || "—"} — {e.end_date || "—"}</div>
                </div>
                <button onClick={() => del(e.id)} className="text-gray-400 hover:text-red-600" data-testid={`delete-event-${e.slug}`}><Trash2 size={14} /></button>
              </div>
              <div className="mt-4 flex gap-2">
                <Btn variant="secondary" onClick={() => nav(`/dashboard/events/${e.id}`)} testId={`open-event-${e.slug}`}><QrCode size={14} /> QR & Details</Btn>
              </div>
            </div>
          ))}
        </div>
      }
      <Modal open={modal} onClose={() => setModal(false)} title="Create event">
        <form onSubmit={create} className="space-y-3" data-testid="new-event-form">
          <Input label="Event name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="event-name" />
          <Input label="Slug (optional)" value={form.slug} onChange={(v) => setForm({ ...form, slug: v })} placeholder="mining-expo-2026" testId="event-slug" />
          <Input label="Venue" value={form.venue} onChange={(v) => setForm({ ...form, venue: v })} testId="event-venue" />
          <Input label="Description" rows={3} value={form.description} onChange={(v) => setForm({ ...form, description: v })} testId="event-description" />
          <div className="grid grid-cols-2 gap-3">
            <Input label="Start date" type="date" value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v })} testId="event-start" />
            <Input label="End date" type="date" value={form.end_date} onChange={(v) => setForm({ ...form, end_date: v })} testId="event-end" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.14em] text-gray-500">Areas of interest (shown in lead form)</label>
            <div className="flex flex-wrap gap-2 mt-2">
              {form.areas_of_interest.map((a, i) => (
                <span key={i} className="inline-flex items-center gap-2 bg-purple-50 text-purple-800 rounded-full px-3 py-1 text-xs">
                  {a}
                  <button type="button" onClick={() => setForm({ ...form, areas_of_interest: form.areas_of_interest.filter((_, idx) => idx !== i) })} className="text-purple-400 hover:text-purple-700">×</button>
                </span>
              ))}
            </div>
            <div className="flex gap-2 mt-2">
              <input value={newArea} onChange={(ev) => setNewArea(ev.target.value)} placeholder="e.g. ChatFlo Automation" className="flex-1 border border-gray-200 rounded-md px-3 py-2 text-sm" data-testid="event-new-area" />
              <Btn variant="secondary" onClick={() => { const v = newArea.trim(); if (v && !form.areas_of_interest.includes(v)) { setForm({ ...form, areas_of_interest: [...form.areas_of_interest, v] }); } setNewArea(""); }} testId="event-add-area">Add</Btn>
            </div>
          </div>
          <div className="flex justify-end gap-2"><Btn variant="secondary" onClick={() => setModal(false)}>Cancel</Btn><Btn type="submit" testId="submit-new-event">Create</Btn></div>
        </form>
      </Modal>
    </DashboardShell>
  );
}

function EventDetail() {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [publicUrl, setPublicUrl] = useState("");
  const [qrSrc, setQrSrc] = useState("");
  const [newArea, setNewArea] = useState("");
  const [saving, setSaving] = useState(false);

  async function refresh() {
    const { data: ev } = await api.get(`/events/${eventId}`);
    setEvent(ev);
    const { data: urlRes } = await api.get(`/events/${eventId}/public-url`);
    setPublicUrl(urlRes.url);
    // Fetch QR PNG as blob with auth
    const qrRes = await api.get(`/events/${eventId}/qr.png`, { responseType: "blob" });
    setQrSrc(URL.createObjectURL(qrRes.data));
  }
  useEffect(() => { refresh(); return () => { if (qrSrc) URL.revokeObjectURL(qrSrc); }; /* eslint-disable-next-line */ }, [eventId]);

  function copy() {
    navigator.clipboard.writeText(publicUrl);
    toast.success("Link copied");
  }
  async function downloadPng() {
    const res = await api.get(`/events/${eventId}/qr.png`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = `${event.slug}-qr.png`; a.click();
    URL.revokeObjectURL(url);
  }
  async function downloadSvg() {
    const res = await api.get(`/events/${eventId}/qr.svg`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = `${event.slug}-qr.svg`; a.click();
    URL.revokeObjectURL(url);
  }
  async function saveAreas() {
    setSaving(true);
    try {
      const { data } = await api.patch(`/events/${eventId}`, { areas_of_interest: event.areas_of_interest || [] });
      setEvent((e) => ({ ...e, ...data }));
      toast.success("Areas of interest saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  }

  if (!event) return <DashboardShell title="Events" subtitle="Event" nav={NAV}><div className="text-sm text-gray-500">Loading…</div></DashboardShell>;

  return (
    <DashboardShell title="Events" subtitle={event.name} nav={NAV}>
      <div className="grid md:grid-cols-2 gap-8 max-w-5xl">
        <div>
          <SectionTitle title="Public URL (encoded in QR)" />
          <div className="border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-700 font-mono break-all" data-testid="event-public-url">{publicUrl || "—"}</div>
          <div className="flex gap-2 mt-3">
            <Btn variant="secondary" onClick={copy} testId="copy-url"><Copy size={14} /> Copy</Btn>
            {publicUrl && <a href={publicUrl} target="_blank" rel="noreferrer"><Btn variant="secondary" testId="open-url"><ExternalLink size={14} /> Open</Btn></a>}
          </div>

          <div className="mt-8">
            <SectionTitle title="Event details" />
            <div className="space-y-2 text-sm">
              <div><span className="text-gray-500">Venue:</span> {event.venue || "—"}</div>
              <div><span className="text-gray-500">Dates:</span> {event.start_date || "—"} to {event.end_date || "—"}</div>
              <div><span className="text-gray-500">Status:</span> {event.status}</div>
              <div><span className="text-gray-500">Description:</span> {event.description || "—"}</div>
            </div>
          </div>

          <div className="mt-8">
            <SectionTitle title="Areas of interest" />
            <p className="text-xs text-gray-500 -mt-2 mb-3">These options appear as a dropdown in the visitor lead form.</p>
            <div className="flex flex-wrap gap-2 mb-3">
              {(event.areas_of_interest || []).map((a, i) => (
                <span key={i} className="inline-flex items-center gap-2 bg-purple-50 text-purple-800 rounded-full px-3 py-1 text-xs">
                  {a}
                  <button onClick={() => setEvent({ ...event, areas_of_interest: event.areas_of_interest.filter((_, idx) => idx !== i) })} className="text-purple-400 hover:text-purple-700">×</button>
                </span>
              ))}
              {(event.areas_of_interest || []).length === 0 && (
                <span className="text-xs text-gray-400">No areas defined yet. The lead form will fall back to a free-text field.</span>
              )}
            </div>
            <div className="flex gap-2">
              <input value={newArea} onChange={(e) => setNewArea(e.target.value)} placeholder="e.g. ChatFlo Automation" className="flex-1 border border-gray-200 rounded-md px-3 py-2 text-sm" data-testid="event-detail-new-area" />
              <Btn variant="secondary" onClick={() => { const v = newArea.trim(); if (v && !(event.areas_of_interest || []).includes(v)) { setEvent({ ...event, areas_of_interest: [...(event.areas_of_interest || []), v] }); } setNewArea(""); }} testId="event-detail-add-area">Add</Btn>
            </div>
            <Btn className="mt-3" onClick={saveAreas} disabled={saving} testId="save-areas">{saving ? "Saving…" : "Save areas"}</Btn>
          </div>
        </div>
        <div>
          <SectionTitle title="QR code" />
          <div className="border border-gray-200 rounded-xl p-6 flex flex-col items-center">
            {qrSrc ? <img src={qrSrc} alt="Event QR" className="w-56 h-56" data-testid="event-qr-img" /> : <div className="w-56 h-56 bg-gray-50 rounded-md animate-pulse" />}
            <div className="text-xs text-gray-500 mt-4 text-center">Scan to open the visitor experience.</div>
            <div className="flex gap-2 mt-4">
              <Btn variant="secondary" onClick={downloadPng} testId="download-png"><Download size={14} /> PNG</Btn>
              <Btn variant="secondary" onClick={downloadSvg} testId="download-svg"><Download size={14} /> SVG</Btn>
            </div>
          </div>
        </div>
      </div>
    </DashboardShell>
  );
}

/* ==================== Knowledge Base ==================== */
function KnowledgeBase() {
  const [rows, setRows] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [faqOpen, setFaqOpen] = useState(false);
  const [faq, setFaq] = useState({ question: "", answer: "" });
  const fileRef = useRef();

  async function refresh() { const { data } = await api.get("/kb"); setRows(data); }
  useEffect(() => { refresh(); }, []);

  async function onFile(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    const me = (await api.get("/auth/me")).data;
    const fd = new FormData();
    fd.append("org_id", me.org_id);
    fd.append("file", f);
    try { await api.post("/kb/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Uploaded and indexed"); refresh(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  }
  async function addFaq(e) {
    e.preventDefault();
    try { await api.post("/kb/faq", faq); toast.success("FAQ added"); setFaqOpen(false); setFaq({ question: "", answer: "" }); refresh(); }
    catch { toast.error("Failed to add FAQ"); }
  }
  async function del(id) { if (!window.confirm("Delete document?")) return; await api.delete(`/kb/${id}`); refresh(); }

  return (
    <DashboardShell title="Knowledge Base" subtitle="Knowledge Base" nav={NAV}>
      <SectionTitle title="Documents & FAQs" action={
        <div className="flex gap-2">
          <Btn variant="secondary" onClick={() => setFaqOpen(true)} testId="add-faq-btn"><Plus size={14} /> Add FAQ</Btn>
          <input ref={fileRef} type="file" accept=".pdf,.docx,.txt,.md" onChange={onFile} className="hidden" data-testid="kb-file-input" />
          <Btn onClick={() => fileRef.current?.click()} disabled={uploading} testId="upload-file-btn"><Upload size={14} /> {uploading ? "Uploading…" : "Upload file"}</Btn>
        </div>
      } />

      {rows.length === 0 ? <EmptyState icon={<FileText size={18} />} title="No knowledge yet" body="Upload PDFs, DOCX or TXT — or add FAQs — so your assistant can answer visitors." /> :
        <div className="border border-gray-200 rounded-xl overflow-x-auto">
          <table className="w-full text-sm min-w-[560px]">
            <thead className="bg-gray-50 border-b border-gray-200"><tr className="text-left text-[10px] uppercase tracking-[0.14em] text-gray-500 font-bold"><th className="px-4 py-3">Name</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Size</th><th className="px-4 py-3">Added</th><th className="px-4 py-3"></th></tr></thead>
            <tbody className="divide-y divide-gray-200">
              {rows.map((r) => (
                <tr key={r.id}>
                  <td className="px-4 py-3 font-medium text-gray-900">{r.filename}</td>
                  <td className="px-4 py-3 text-gray-600 uppercase text-xs">{r.kind}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{r.size ? `${(r.size / 1024).toFixed(1)} KB` : "—"}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{r.created_at?.slice(0, 10)}</td>
                  <td className="px-4 py-3 text-right"><button onClick={() => del(r.id)} className="text-gray-400 hover:text-red-600" data-testid={`delete-kb-${r.id}`}><Trash2 size={14} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      }

      <Modal open={faqOpen} onClose={() => setFaqOpen(false)} title="Add FAQ">
        <form onSubmit={addFaq} className="space-y-3">
          <Input label="Question" value={faq.question} onChange={(v) => setFaq({ ...faq, question: v })} required testId="faq-question" />
          <Input label="Answer" rows={5} value={faq.answer} onChange={(v) => setFaq({ ...faq, answer: v })} required testId="faq-answer" />
          <div className="flex justify-end gap-2"><Btn variant="secondary" onClick={() => setFaqOpen(false)}>Cancel</Btn><Btn type="submit" testId="submit-faq">Add</Btn></div>
        </form>
      </Modal>
    </DashboardShell>
  );
}

/* ==================== Leads ==================== */
function LeadsList() {
  const [rows, setRows] = useState([]);
  const [exporting, setExporting] = useState(false);
  useEffect(() => { api.get("/leads").then((r) => setRows(r.data)); }, []);

  async function exportCsv() {
    setExporting(true);
    try {
      const res = await api.get("/leads/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `leads-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Exported");
    } catch { toast.error("Export failed"); }
    finally { setExporting(false); }
  }

  return (
    <DashboardShell title="Leads" subtitle="Leads" nav={NAV}>
      <SectionTitle title="Captured leads" action={
        rows.length > 0 ? <Btn variant="secondary" onClick={exportCsv} disabled={exporting} testId="export-leads-csv"><Download size={14} /> {exporting ? "Exporting…" : "Export CSV"}</Btn> : null
      } />
      {rows.length === 0 ? <EmptyState icon={<Users size={18} />} title="No leads yet" body="Leads appear here when visitors take an action." /> :
        <div className="border border-gray-200 rounded-xl overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-gray-50 border-b border-gray-200"><tr className="text-left text-[10px] uppercase tracking-[0.14em] text-gray-500 font-bold"><th className="px-4 py-3">Name</th><th className="px-4 py-3">Company</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Phone</th><th className="px-4 py-3">Action</th><th className="px-4 py-3">Interest</th><th className="px-4 py-3">Received</th></tr></thead>
            <tbody className="divide-y divide-gray-200">
              {rows.map((l) => (
                <tr key={l.id}>
                  <td className="px-4 py-3 font-medium text-gray-900">{l.full_name}</td>
                  <td className="px-4 py-3 text-gray-700">{l.company || "—"}</td>
                  <td className="px-4 py-3 text-gray-700">{l.email || "—"}</td>
                  <td className="px-4 py-3 text-gray-700">{l.phone || "—"}</td>
                  <td className="px-4 py-3"><span className="text-xs px-2 py-1 rounded-md bg-purple-50 text-purple-700">{l.action || "—"}</span></td>
                  <td className="px-4 py-3 text-gray-700">{l.area_of_interest || "—"}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{l.created_at?.slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      }
    </DashboardShell>
  );
}

/* ==================== AI Settings ==================== */
function AISettings() {
  const [org, setOrg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newQ, setNewQ] = useState("");
  const logoRef = useRef();
  const bannerRef = useRef();
  const [uploading, setUploading] = useState(null); // "logo" | "banner" | null

  useEffect(() => { api.get("/auth/me").then(async (u) => { const o = await api.get(`/organizations/${u.data.org_id}`); setOrg(o.data); }); }, []);

  async function save() {
    setSaving(true);
    try {
      const { data } = await api.patch(`/organizations/${org.id}`, {
        assistant_name: org.assistant_name, welcome_message: org.welcome_message,
        system_instructions: org.system_instructions, suggested_questions: org.suggested_questions,
        voice_enabled: org.voice_enabled, lead_capture_enabled: org.lead_capture_enabled,
        brand_color: org.brand_color,
      });
      setOrg({ ...data, logo_path: org.logo_path, banner_path: org.banner_path });
      toast.success("Saved");
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  }

  async function uploadImage(kind, file) {
    if (!file) return;
    setUploading(kind);
    const fd = new FormData();
    fd.append("org_id", org.id);
    fd.append("kind", kind);
    fd.append("file", file);
    try {
      const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setOrg((o) => ({ ...o, [kind === "logo" ? "logo_path" : "banner_path"]: data.path }));
      toast.success(`${kind === "logo" ? "Logo" : "Banner"} uploaded`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(null); }
  }

  if (!org) return <DashboardShell title="AI Settings" subtitle="AI Settings" nav={NAV}><div>Loading…</div></DashboardShell>;

  return (
    <DashboardShell title="AI Settings" subtitle="AI Settings" nav={NAV}>
      <div className="max-w-3xl space-y-6">
        <SectionTitle title="Assistant" />
        <Input label="Assistant name" value={org.assistant_name} onChange={(v) => setOrg({ ...org, assistant_name: v })} testId="ai-assistant-name" />
        <Input label="Welcome message" rows={3} value={org.welcome_message} onChange={(v) => setOrg({ ...org, welcome_message: v })} testId="ai-welcome" />
        <Input label="System instructions" rows={5} value={org.system_instructions} onChange={(v) => setOrg({ ...org, system_instructions: v })} testId="ai-instructions" />
        <div>
          <label className="text-[10px] font-bold uppercase tracking-[0.14em] text-gray-500">Suggested questions</label>
          <div className="flex flex-wrap gap-2 mt-2">
            {(org.suggested_questions || []).map((q, i) => (
              <span key={i} className="inline-flex items-center gap-2 bg-purple-50 text-purple-800 rounded-full px-3 py-1 text-xs">
                {q}
                <button onClick={() => setOrg({ ...org, suggested_questions: org.suggested_questions.filter((_, idx) => idx !== i) })} className="text-purple-400 hover:text-purple-700">×</button>
              </span>
            ))}
          </div>
          <div className="flex gap-2 mt-3">
            <input value={newQ} onChange={(e) => setNewQ(e.target.value)} placeholder="Add a suggested question…" className="flex-1 border border-gray-200 rounded-md px-3 py-2 text-sm" data-testid="ai-new-suggested" />
            <Btn variant="secondary" onClick={() => { if (newQ.trim()) { setOrg({ ...org, suggested_questions: [...(org.suggested_questions || []), newQ.trim()] }); setNewQ(""); } }} testId="ai-add-suggested">Add</Btn>
          </div>
        </div>
        <div className="flex items-center gap-6">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={org.voice_enabled} onChange={(e) => setOrg({ ...org, voice_enabled: e.target.checked })} data-testid="ai-voice-enabled" /> Voice enabled
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={org.lead_capture_enabled} onChange={(e) => setOrg({ ...org, lead_capture_enabled: e.target.checked })} data-testid="ai-lead-capture" /> Lead capture enabled
          </label>
        </div>

        <SectionTitle title="Branding" />
        <div className="grid md:grid-cols-2 gap-4">
          <div className="border border-gray-200 rounded-xl p-4">
            <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-gray-500 mb-3">Logo</div>
            <div className="h-24 rounded-md bg-gray-50 border border-gray-200 flex items-center justify-center overflow-hidden">
              {org.logo_path ? <img src={fileUrl(org.logo_path)} alt="logo" className="max-h-20 max-w-full object-contain" /> : <span className="text-gray-400 text-xs">No logo</span>}
            </div>
            <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage("logo", e.target.files?.[0])} data-testid="logo-file-input" />
            <Btn variant="secondary" className="mt-3" onClick={() => logoRef.current?.click()} disabled={uploading === "logo"} testId="upload-logo-btn">
              <ImageIcon size={14} /> {uploading === "logo" ? "Uploading…" : (org.logo_path ? "Replace logo" : "Upload logo")}
            </Btn>
          </div>
          <div className="border border-gray-200 rounded-xl p-4">
            <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-gray-500 mb-3">Visitor banner background</div>
            <div className="h-24 rounded-md bg-gray-50 border border-gray-200 flex items-center justify-center overflow-hidden">
              {org.banner_path ? <img src={fileUrl(org.banner_path)} alt="banner" className="w-full h-full object-cover" /> : <span className="text-gray-400 text-xs">No banner</span>}
            </div>
            <input ref={bannerRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage("banner", e.target.files?.[0])} data-testid="banner-file-input" />
            <Btn variant="secondary" className="mt-3" onClick={() => bannerRef.current?.click()} disabled={uploading === "banner"} testId="upload-banner-btn">
              <ImageIcon size={14} /> {uploading === "banner" ? "Uploading…" : (org.banner_path ? "Replace banner" : "Upload banner")}
            </Btn>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Input label="Brand color (hex)" value={org.brand_color} onChange={(v) => setOrg({ ...org, brand_color: v })} testId="ai-brand-color" />
          <div className="flex items-end">
            <div className="w-full h-10 rounded-md" style={{ background: `linear-gradient(135deg, ${org.brand_color}, #7C3AED)` }} />
          </div>
        </div>
        <Btn onClick={save} disabled={saving} testId="save-ai-settings">{saving ? "Saving…" : "Save changes"}</Btn>
      </div>
    </DashboardShell>
  );
}

/* ==================== Analytics ==================== */
function Analytics() {
  const [stats, setStats] = useState(null);
  const [series, setSeries] = useState([]);
  const [days, setDays] = useState(14);
  useEffect(() => { api.get("/analytics").then((r) => setStats(r.data)); }, []);
  useEffect(() => { api.get(`/analytics/timeseries?days=${days}`).then((r) => setSeries(r.data.series || [])); }, [days]);

  return (
    <DashboardShell title="Analytics" subtitle="Analytics" nav={NAV}>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Events" value={stats?.events} testId="a-stat-events" />
        <StatCard label="Conversations" value={stats?.conversations} testId="a-stat-convs" />
        <StatCard label="Questions" value={stats?.questions_asked} testId="a-stat-q" />
        <StatCard label="Leads" value={stats?.leads} testId="a-stat-leads" />
      </div>

      <div className="mt-8 border border-gray-200 rounded-xl p-4 md:p-6" data-testid="analytics-chart">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-gray-400">Activity trend</div>
            <div className="font-display text-lg font-semibold text-gray-900 mt-1">Last {days} days</div>
          </div>
          <div className="flex gap-1 border border-gray-200 rounded-md p-0.5">
            {[7, 14, 30].map((d) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={`px-3 py-1 text-xs rounded ${days === d ? "bg-gray-900 text-white" : "text-gray-600 hover:bg-gray-50"}`}
                data-testid={`range-${d}d`}
              >
                {d}d
              </button>
            ))}
          </div>
        </div>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={series} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
              <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} fontSize={11} stroke="#9CA3AF" />
              <YAxis fontSize={11} stroke="#9CA3AF" allowDecimals={false} />
              <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="conversations" stroke="#7C3AED" strokeWidth={2} dot={false} name="Conversations" />
              <Line type="monotone" dataKey="questions" stroke="#E91E93" strokeWidth={2} dot={false} name="Questions" />
              <Line type="monotone" dataKey="leads" stroke="#10B981" strokeWidth={2} dot={false} name="Leads" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="mt-8">
        <SectionTitle title="Most popular questions" />
        {(stats?.popular_questions || []).length === 0 ? <EmptyState title="No questions yet" body="Once visitors start asking, this list will populate." /> :
          <div className="border border-gray-200 rounded-xl overflow-x-auto">
            <table className="w-full text-sm min-w-[420px]">
              <thead className="bg-gray-50 border-b border-gray-200"><tr className="text-left text-[10px] uppercase tracking-[0.14em] text-gray-500 font-bold"><th className="px-4 py-3">Question</th><th className="px-4 py-3 w-24 text-right">Count</th></tr></thead>
              <tbody className="divide-y divide-gray-200">
                {stats.popular_questions.map((q, i) => (
                  <tr key={i}><td className="px-4 py-3 text-gray-800">{q.question}</td><td className="px-4 py-3 text-right text-gray-700 font-mono">{q.count}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        }
      </div>
    </DashboardShell>
  );
}


/* ==================== Conversations (Visitor Insights) ==================== */
function ConversationsList() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/conversations").then((r) => { setRows(r.data); setLoading(false); });
  }, []);

  return (
    <DashboardShell title="Conversations" subtitle="Visitor Conversations" nav={NAV}>
      <SectionTitle title="Recent transcripts" />
      {loading ? <div className="text-sm text-gray-500">Loading…</div> :
       rows.length === 0 ? <EmptyState icon={<MessageSquare size={18} />} title="No conversations yet" body="Once a visitor scans a QR and chats with your assistant, you'll see the full transcript here." /> :
      <div className="border border-gray-200 rounded-xl overflow-hidden divide-y divide-gray-200">
        {rows.map((c) => (
          <button
            key={c.id}
            onClick={() => nav(`/dashboard/conversations/${c.id}`)}
            className="w-full text-left px-5 py-4 hover:bg-gray-50 transition-colors flex items-start gap-4"
            data-testid={`open-conversation-${c.id}`}
          >
            <div className="w-9 h-9 rounded-full bg-purple-50 text-purple-700 flex items-center justify-center shrink-0">
              <UserIcon size={16} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium text-gray-900 truncate">{c.first_message || "(no message)"}</span>
                {c.leads?.length > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-50 text-green-700 uppercase tracking-wider font-bold">Lead</span>}
              </div>
              <div className="text-xs text-gray-500 mt-1 flex items-center gap-3 flex-wrap">
                <span>{c.event_name || "Direct link"}</span>
                <span>·</span>
                <span>{c.question_count} question{c.question_count === 1 ? "" : "s"}</span>
                <span>·</span>
                <span>{(c.last_message_at || c.created_at)?.slice(0, 10)}</span>
              </div>
            </div>
            <div className="text-gray-300 text-xs mt-2">→</div>
          </button>
        ))}
      </div>
      }
    </DashboardShell>
  );
}

function ConversationDetail() {
  const { convId } = useParams();
  const nav = useNavigate();
  const [conv, setConv] = useState(null);
  useEffect(() => { api.get(`/conversations/${convId}`).then((r) => setConv(r.data)); }, [convId]);

  if (!conv) return <DashboardShell title="Conversations" subtitle="Transcript" nav={NAV}><div>Loading…</div></DashboardShell>;

  return (
    <DashboardShell title="Conversations" subtitle="Transcript" nav={NAV}>
      <div className="max-w-3xl">
        <button onClick={() => nav("/dashboard/conversations")} className="text-sm text-gray-500 hover:text-gray-900 inline-flex items-center gap-1.5" data-testid="back-to-conversations">
          <ArrowLeft size={14} /> All conversations
        </button>

        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Messages" value={conv.messages?.length || 0} />
          <StatCard label="Questions" value={(conv.messages || []).filter((m) => m.role === "user").length} />
          <StatCard label="Event" value={conv.event?.name?.slice(0, 20) || "—"} />
          <StatCard label="Leads captured" value={conv.leads?.length || 0} />
        </div>

        {conv.leads?.length > 0 && (
          <div className="mt-8">
            <SectionTitle title="Captured lead" />
            {conv.leads.map((l) => (
              <div key={l.id} className="border border-gray-200 rounded-xl p-4 space-y-1 text-sm mb-3" data-testid={`conv-lead-${l.id}`}>
                <div><span className="text-gray-500">Name:</span> <span className="font-medium">{l.full_name}</span></div>
                {l.company && <div><span className="text-gray-500">Company:</span> {l.company}</div>}
                {l.email && <div><span className="text-gray-500">Email:</span> {l.email}</div>}
                {l.phone && <div><span className="text-gray-500">Phone:</span> {l.phone}</div>}
                {l.area_of_interest && <div><span className="text-gray-500">Interest:</span> {l.area_of_interest}</div>}
                <div><span className="text-gray-500">Action:</span> <span className="text-xs px-2 py-0.5 rounded-md bg-purple-50 text-purple-700">{l.action}</span></div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-8">
          <SectionTitle title="Full transcript" />
          <div className="border border-gray-200 rounded-xl divide-y divide-gray-100" data-testid="transcript">
            {(conv.messages || []).map((m, i) => (
              <div key={i} className="flex gap-3 px-5 py-4">
                <div className={`w-8 h-8 rounded-full shrink-0 flex items-center justify-center ${m.role === "user" ? "bg-purple-50 text-purple-700" : "bg-gray-900 text-white"}`}>
                  {m.role === "user" ? <UserIcon size={14} /> : <Bot size={14} />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-gray-400">
                    {m.role === "user" ? "Visitor" : "Assistant"}
                    <span className="ml-2 font-normal text-gray-400 normal-case tracking-normal">{(m.ts || "").slice(11, 16)}</span>
                  </div>
                  {m.role === "assistant" ? (
                    <div className="prose prose-sm max-w-none text-gray-800 mt-1">
                      <ReactMarkdown>{m.text}</ReactMarkdown>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-800 mt-1">{m.text}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </DashboardShell>
  );
}
